import { Injectable, NgZone } from "@angular/core";
import { Subject, Observable } from "rxjs";
import { tap } from "rxjs/operators";
import _ from "lodash";
import * as moment from "moment";
import { HttpClient } from "@angular/common/http";
import { NzModalService } from "ng-zorro-antd/modal";
import { Router } from "@angular/router";
import { StoreService } from "@root/shared/service/store.service";
import { SystemService } from "@root/router/system/system.service";
import { PAY_EDITION } from "@root/config";
import { TranslateService } from "../../shared/service/translate.service";
import { DateService } from "../../shared/service/date.service";
import { LoginService } from "@root/router/login/login.service";
import { ACLService } from "@delon/acl";

interface CouponInfo {
  currency_type: string;
  amount: number;
  create_time: string;
  expire_time: string;
  expire_day: number;
  history_house_count: number;
  register_date: string;
  register_day: number;
}
interface IHttpResponse {
  error_code: number;
  error_msg: string;
  data: any;
}
export interface IEditionStatus {
  status: number;
  house_quantity: number;
  quantity?: number;
  start_time?: string;
  expire_time?: string;
  edition?: number;
  current?: any;
}

export enum EditionStatus {
  Normal = 1,
  Expired = 2,
  Exceed_In_Limit = 3,
  Exceed_Out_Limit = 4,
}

const MEMBER_DETAIL_API = "/api/member/detail";
const COUPON_TAKE_API = "/api/member/coupon/take";
const CASH_BACK_RATE_API = "/api/member/friend_invitation/cashback_rate";

/**
 * @deprecated 1，功能重复 2，命名模糊
 * @todo 合并到支付服务
 * @FIXME 该模块放置全局通用服务，不涉及业务逻辑。此服务需放置到具体业务模块下
 */

@Injectable({
  providedIn: "root",
})
export class MemberService {
  /** 当前合约状态 */
  editionInfo: IEditionStatus = {
    status: null,
    house_quantity: 0,
    /** start @deprecated */
    quantity: 0,
    start_time: null,
    expire_time: null,
    edition: null,
    /** end */
    current: null,
  };
  editionStatusRaw$ = new Subject<IEditionStatus>();

  private editionStatusChange$ = this.editionStatusRaw$.pipe(
    tap((value) => (this.editionInfo = value))
  );

  private logout$ = this.loginService.logout$.pipe(
    tap(() => {
      this.editionInfo = {
        status: null,
        house_quantity: 0,
        quantity: 0,
        start_time: null,
        expire_time: null,
        edition: null,
        current: null,
      };
      [0, 1, 3, 7, 15, 30].forEach(i => {
        this.storeService.remove(`host_has_notified_will_expire_${i}_days_later`, 2);
      });
    })
  );

  get isSubAccount() {
    return this.acl.can({ role: ['slave'] });
    // return _.get(this.systemService, "subAccountInfo.isSubAccount");
  }

  constructor(
    private http: HttpClient,
    private modalService: NzModalService,
    private router: Router,
    private storeService: StoreService,
    private systemService: SystemService,
    private zone: NgZone,
    private translate: TranslateService,
    private dateService: DateService,
    private loginService: LoginService,
    private acl: ACLService
  ) {
    this.editionStatusChange$.subscribe();
    this.logout$.subscribe();
  }

  private isEditionNormal(edition: EditionStatus) {
    return edition === EditionStatus.Normal;
  }

  private isEditionExpired(status: EditionStatus) {
    return status === EditionStatus.Expired;
  }

  private isEditionExceedInLimit(status: EditionStatus) {
    return status === EditionStatus.Exceed_In_Limit;
  }

  private isEditionExceedOutLimit(status: EditionStatus) {
    return status === EditionStatus.Exceed_Out_Limit;
  }

  private isEditionTrial(edition: PAY_EDITION) {
    return edition === PAY_EDITION.EDITION_TRIAL;
  }

  private isEditionFree (edition: PAY_EDITION) {
    return edition === PAY_EDITION.EDITION_FREE;
  }

  isEditionExceedQuantityLimit() {
    const edition = this.editionInfo;

    if (!edition) {
      return false;
    }
    return (
      (this.isEditionNormal(edition.status) &&
        edition.house_quantity >= edition.quantity) ||
      (this.isEditionTrial(edition.edition) && edition.house_quantity >= 10)
    );
  }

  isExpired() {
    const edition = this.editionInfo;

    if (!edition) {
      return true;
    }
    return (
      this.isEditionExpired(edition.status) ||
      this.isEditionExceedOutLimit(edition.status)
    );
  }
  isTrial() {
    const edition = this.editionInfo;

    if (!edition) {
      return true;
    }

    return this.isEditionTrial(edition.edition);
  }

  isFree () {
    const edition = this.editionInfo;

    if (!edition) {
      return false;
    }

    return this.isEditionFree(edition.edition);
  }

  updateEditionStatus(status: IEditionStatus) {
    this.editionStatusRaw$.next(status);
  }

  getCurrentEdition() {
    return this.http.get(MEMBER_DETAIL_API) as Observable<IHttpResponse>;
  }

  getEditionstatus() {
    this.getCurrentEdition().subscribe((res) => {
      if (res.error_code === 0) {
        const status = _.get(res, "data.status");
        const house_quantity = _.get(res, "data.house_quantity");

        ////////////////////////////////
        ////////////////////////////////  @deprecated @todo:
        const quantity = _.get(res, "data.current.quantity");
        // const quantity = 30;
        const start_time = _.get(res, "data.current.start_time");
        const expire_time = _.get(res, "data.current.expire_time");
        // const expire_time = "2020-09-23 23:59:59";
        const edition = _.get(res, "data.current.edition");
        //////////////////////////////// @deprecated
        ////////////////////////////////

        const current = _.get(res, 'data.current') || {};
        // const edition = 1;
        this.updateEditionStatus({
          status,
          house_quantity,
          quantity,
          start_time,
          expire_time,
          edition,
          current,
        });
        if (_.get(res, "data.next.length") === 0) {
          this.checkIsMemberExpired();
        }
      }
    });
  }

  isExpriedEditionForbiddenPath (path) {
    return this.isExpired() && this.inExpiredEditionBlockList(path);
  }

  isTrialEditionForbiddenPath (path) {
    return this.isTrial() && this.inTrialEditionBlockList(path);
  }

  toForbiddenPage (params) {
    this.router.navigate(['/app/blank-page'], { queryParams: params });
  }

  private inExpiredEditionBlockList (path) {
    return [
      "/app/messages",
      "/app/calendar",
      "/app/rates/calendar",
      "/app/tasks",
      "/app/statistics/payment-structure",
      "/app/statistics/operate",
      "/app/shop/list"
    ].indexOf(path) !== -1;
  }

  private inTrialEditionBlockList (path) {
    return [
      "/app/room_types",
      "/app/room_types/detail",
      "/app/tasks",
      "/app/messages",
      "/app/shop/list",
      '/app/robot',
      '/app/rate/list'
    ].indexOf(path) !== -1;
  }

  private checkIsMemberExpired() {
    // const diff = moment(this.editionStatus.expire_time).diff(moment(), "days");
    const diff = this.editionInfo.current.rest_days;
    /** 只有1，3，7天才通知 */
    if ((this.isFree() && [0, 1, 3, 7].indexOf(diff) === -1) || (!this.isFree() && [0, 1, 3, 7, 15, 30].indexOf(diff) === -1)) {
      return;
    }

    if (!this.isNotifiedForSpecifiedDaysBeforeExpire(diff)) {
      this.modalService.warning({
        nzTitle: this.getExpiredModalTitle(diff),
        nzContent: this.getExpiredModalContent(diff),
        nzCancelText: this.translate.syncGet("word.cancel"),
        nzOkText:
          // 付费/续费
          this.editionInfo.edition === 0
            ? this.translate.syncGet("word.pay")
            : this.translate.syncGet("word.renew"),
        nzOnOk: () => this.editionInfo.edition === 0
          ? this.router.navigate(["/app/pay/upgrade"])
          : this.router.navigate(["/app/pay/upgrade"], { queryParams: { target: 2 } }),
      });
      this.markNotifiedForSpecifiedDaysBeforeExpire(diff);
    }


    return;
    switch (diff) {
      case 7:
      case 3:
      case 1:
        if (!this.isNotifiedForSpecifiedDaysBeforeExpire(diff)) {
          this.modalService.warning({
            nzTitle: this.getExpiredModalTitle(diff),
            // nzTitle: this.translate.syncGet(
            //   diff === 1
            //     ? "warning.will_be_expired_in_day"
            //     : "warning.will_be_expired_in_days",
            //   { diff: diff }
            // ),
            // nzTitle: `您的服务将于${diff}天后到期`,
            nzContent: this.getExpiredModalContent(diff),
            // nzContent: warningContent,
            nzCancelText: this.translate.syncGet("word.cancel"),
            nzOkText:
              // 付费/续费
              this.editionInfo.edition === 0
                ? this.translate.syncGet("word.pay")
                : this.translate.syncGet("word.renew"),
            nzOnOk: () => this.editionInfo.edition === 0
              ? this.router.navigate(["/app/pay/upgrade"])
              : this.router.navigate(["/app/pay/upgrade"], { queryParams: { target: 2 } }),
          });
          this.markNotifiedForSpecifiedDaysBeforeExpire(diff);
        }
        break;
      default:
        // this.storeService.set(
        //   `host_has_notified_will_expire_7_days_later`,
        //   false,
        //   2
        // );
        // this.storeService.set(
        //   `host_has_notified_will_expire_3_days_later`,
        //   false,
        //   2
        // );
        // this.storeService.set(
        //   `host_has_notified_will_expire_1_days_later`,
        //   false,
        //   2
        // );
    }
  }

  private isNotifiedForSpecifiedDaysBeforeExpire (day) {
    return this.storeService.get(`host_has_notified_will_expire_${day}_days_later`, 2)
  }
  private markNotifiedForSpecifiedDaysBeforeExpire (day) {
    this.storeService.set(`host_has_notified_will_expire_${day}_days_later`, true, 2);
  }

  private getExpiredModalTitle (leftDays) {
    let key;
    if (leftDays === 0) {
      key = 'warning.will_be_expired_in_today';
    } else if (leftDays === 1) {
      key = 'warning.will_be_expired_in_day';
    } else {
      key = `warning.will_be_expired_in_days`;
    }
    return this.translate.syncGet(key, { diff: leftDays });
  }

  private getExpiredModalContent (leftDays) {
    if (this.isFree()) {
      const key = `warning.will_be_expired_in_${leftDays === 0 ? 'today' : 'day'}_info_for_free`;
      return this.translate.syncGet(key, { diff: leftDays });
    } else {
      let key;
      if (leftDays === 0) {
        key = `warning.will_be_expired_in_today_info_for_room${this.editionInfo.quantity === 1 ? '' : 's'}`;
      } else if (leftDays === 1) {
        key = `warning.will_be_expired_in_day_info_for_room${this.editionInfo.quantity === 1 ? '' : 's'}`;
      } else {
        key = `warning.will_be_expired_in_days_info_for_room${this.editionInfo.quantity === 1 ? '' : 's'}`;
      }
      const edition = this.translate.syncGet('pay.package.edition_with_suf_' + this.editionInfo.edition);
      return this.translate.syncGet(key, { diff: leftDays, edition,  quantity: this.editionInfo.quantity })
    }
  }

  private mainAccountForbidCreateHouseModal() {
    this.modalService.warning({
      // nzTitle: "您的房间数量已经到达您购买的上限，暂时无法新建房间",
      nzTitle: this.translate.syncGet("warning.cannot_create_rooms"),
      nzMaskClosable: false,
      // nzOkText: "升级",
      nzOkText: this.translate.syncGet("word.update"),
      nzCancelText: this.translate.syncGet("word.cancel"),
      nzOnCancel: () => this.router.navigate(["/app/properties"]),
      nzOnOk: () => this.router.navigate(["/app/pay/upgrade"], { queryParams: { target: 4 } }),
    });
  }

  private subAccountForbidCreateHouseModal() {
    this.modalService.warning({
      // nzTitle: `您的房间数量已到达购买的上限，暂时无法新建房间，如需创建，请先通知主账号进行升级操作。`,
      nzTitle: this.translate.syncGet("warning.subaccount_cannot_create_rooms"),
      nzMaskClosable: false,
      // nzOkText: "知道了",
      nzOkText: this.translate.syncGet("word.got_it"),
    });
  }

  displayForbidCreateHouseModal() {
    if (this.isSubAccount) {
      this.subAccountForbidCreateHouseModal();
    } else {
      this.mainAccountForbidCreateHouseModal();
    }
  }

  notifyForPagesNotAllowed() {
    const edition = this.editionInfo;
    const expireDate = this.dateService.format(this.editionInfo.expire_time, [
      "YMD",
    ]);
    const start = this.dateService.format(this.editionInfo.start_time, [
      "YMD",
    ]);
    if (!this.isSubAccount) {
      if (this.isEditionExpired(edition.status)) {
        const editionName = this.translate.syncGet('pay.package.edition_with_suf_' + this.editionInfo.edition);
        this.modalService.warning({
          // nzTitle: `您的产品已于${expireDate}到期，需要先续费才可继续使用。`,
          nzTitle: this.translate.syncGet("warning.edition_expired", {
            expireDate: expireDate,
            edition: editionName
          }),
          nzMaskClosable: false,
          // nzOkText: "续费",
          nzOkText: this.translate.syncGet("word.renew"),
          nzCancelText: this.translate.syncGet("word.cancel"),
          nzOnCancel: () => this.router.navigate(["/app/properties"]),
          nzOnOk: () => this.router.navigate(["/app/pay/upgrade"], { queryParams: { target: 2 } }),
        });
      } else if (this.isEditionExceedOutLimit(edition.status)) {
        this.modalService.warning({
          //   nzTitle: `从${moment(this.editionStatus.start_time).format(
          //     "YYYY年MM月DD日"
          //   )}开始，
          // 您的房间数上限为${this.editionStatus.quantity}，目前已超上限。
          // 请立即调整房间数量或升级以恢复使用。`,
          nzTitle: this.translate.syncGet("warning.too_many_rooms", {
            start: start,
            quantity: this.editionInfo.quantity,
          }),
          nzMaskClosable: false,
          // nzOkText: "升级",
          nzOkText: this.translate.syncGet("word.update"),
          // nzCancelText: "调整房间",
          nzCancelText: this.translate.syncGet("pay.adjust_the_room"),
          nzOnCancel: () => this.router.navigate(["/app/properties"]),
          nzOnOk: () => this.router.navigate(["/app/pay/upgrade"], { queryParams: { target: 4 } }),
        });
      }
    } else {
      if (this.isEditionExpired(edition.status)) {
        this.modalService.warning({
          // nzTitle: `您的产品已于${expireDate}到期，请尽快通知主账号续费。`,
          nzTitle: this.translate.syncGet(
            "warning.subaccount_edition_expired",
            {
              expireDate: expireDate,
            }
          ),
          nzMaskClosable: false,
          // nzOkText: "知道了",
          nzOkText: this.translate.syncGet("word.got_it"),
          nzOnOk: () => this.router.navigate(["/app/properties"]),
        });
      } else if (this.isEditionExceedOutLimit(edition.status)) {
        this.modalService.warning({
          //   nzTitle: `从${moment(this.editionStatus.start_time).format(
          //     "YYYY年MM月DD日"
          //   )}开始，
          // 您的房间数上限为${this.editionStatus.quantity}，目前已超上限。
          // 请通知主账号立即调整房间数量或升级，以恢复使用。`,
          nzTitle: this.translate.syncGet(
            "warning.subaccount_too_many_houses",
            {
              start: start,
              quantity: this.editionInfo.quantity,
            }
          ),
          nzMaskClosable: false,
          // nzOkText: "知道了",
          nzOkText: this.translate.syncGet("word.got_it"),
          nzOnOk: () => this.router.navigate(["/app/properties"]),
        });
      }
    }
  }

  notifyForTrial() {
    if (this.editionInfo.edition === PAY_EDITION.EDITION_TRIAL) {
      if (!this.isSubAccount) {
        this.modalService.warning({
          // nzTitle: "体验版暂不支持此功能，如需使用，请先升级。",
          nzTitle: this.translate.syncGet("warning.trial_not_supported"),
          nzMaskClosable: false,
          // nzOkText: "升级",
          nzOkText: this.translate.syncGet("word.update"),
          nzCancelText: this.translate.syncGet("word.cancel"),
          nzOnCancel: () => this.router.navigate(["/app/properties"]),
          nzOnOk: () => this.router.navigate(["/app/pay/upgrade"], { queryParams: { target: 8 } }),
        });
      } else {
        this.modalService.warning({
          // nzTitle:
          //   "体验版暂不支持此功能，如需使用，请先通知主账号进行升级操作。",
          nzTitle: this.translate.syncGet(
            "warning.subaccount_trial_not_supported"
          ),
          nzMaskClosable: false,
          // nzOkText: "知道了",
          nzOkText: this.translate.syncGet("word.got_it"),
          nzOnOk: () => this.router.navigate(["/app/properties"]),
        });
      }
    }
  }

  getCoupon() {
    return this.http.get(COUPON_TAKE_API) as Observable<IHttpResponse>;
  }

  getCashBackRate() {
    return this.http.get(CASH_BACK_RATE_API) as Observable<IHttpResponse>;
  }

  // judgeShowStartChargingModalOrNot() {
  //   const opid = this.storeService.get("operator_id", 1);
  //   const hasLoggedIn = this.storeService.get(
  //     `${opid}_register_before_2019_12_17_has_logged_in`,
  //     2
  //   );
  //   if (!hasLoggedIn) {
  //     this.getCoupon().subscribe((res) => {
  //       const couponInfo: CouponInfo = _.get(res, "data.coupon");
  //       const houseQuantity = this.editionStatus.house_quantity;
  //       if (couponInfo) {
  //         if (
  //           moment(couponInfo.register_date).diff(
  //             moment("2019-12-17"),
  //             "days"
  //           ) < 0 &&
  //           houseQuantity > 0
  //         ) {
  //           this.getCashBackRate().subscribe((p) => {
  //             if (p.error_code === 0) {
  //               const rate = _.get(p, "data.rate");
  //               setTimeout(() => {
  //                 // 防止用户在10秒之内退出还弹窗，这个时候再判断一次是否登录，没登录就不弹了
  //                 const currentOpid = this.storeService.get("operator_id", 1);
  //                 if (!currentOpid) {
  //                   return;
  //                 }
  //                 this.modalService.create({
  //                   nzTitle: null,
  //                   nzFooter: null,
  //                   nzContent: StartChargingNotifyComponent,
  //                   nzComponentParams: {
  //                     couponInfo: couponInfo,
  //                     rate: rate,
  //                   },
  //                 });
  //                 this.storeService.set(
  //                   `${opid}_register_before_2019_12_17_has_logged_in`,
  //                   true,
  //                   2
  //                 );
  //               }, 8000);
  //             }
  //           });
  //         }
  //       }
  //     });
  //   }
  // }

  /** @deprecated 不明所以 */
  judgelowerContractNotifyInSevenDaysOrNot() {
    const today = moment().format("YYYY-MM-DD");
    const opid = this.storeService.get("operator_id", 1);
    const hasNotifiedDay = this.storeService.get(
      `${opid}_lower_contract_in_seven_days_has_notified`,
      2
    );
    if (hasNotifiedDay && hasNotifiedDay === today) {
      return;
    }
    const editionStatus = this.editionInfo;
    const start = this.dateService.format(editionStatus.start_time, ["YMD"]);
    if (editionStatus.status === EditionStatus.Exceed_In_Limit) {
      this.storeService.set(
        `${opid}_lower_contract_in_seven_days_has_notified`,
        today,
        2
      );
      if (!this.isSubAccount) {
        this.modalService.warning({
          //   nzTitle: `从${moment(editionStatus.start_time).format(
          //     "YYYY年MM月DD日"
          //   )}开始，
          // 您的房间数上限为${editionStatus.quantity}，目前已超上限。
          // 请于7天内调整房间数量或升级以免服务中止。`,
          nzTitle: this.translate.syncGet("warning.adjust_the_room_in_7_days", {
            start: start,
            quantity: editionStatus.quantity,
          }),
          // nzOkText: "升级",
          nzOkText: this.translate.syncGet("word.update"),
          // nzCancelText: "调整房间",
          nzCancelText: this.translate.syncGet("pay.adjust_the_room"),
          nzMaskClosable: false,
          nzOnCancel: () => this.router.navigate(["/app/properties"]),
          nzOnOk: () => this.router.navigate(["/app/pay/upgrade"]),
        });
      } else {
        this.modalService.warning({
          //   nzTitle: `从${moment(editionStatus.start_time).format(
          //     "YYYY年MM月DD日"
          //   )}开始，
          // 您的房间数上限为${editionStatus.quantity}，目前已超上限。
          // 请通知主账号于7天内调整房间数量或升级，以免服务中止。`,
          nzTitle: this.translate.syncGet(
            "warning.subaccount_adjust_the_room_in_7_days",
            {
              start: start,
              quantity: editionStatus.quantity,
            }
          ),
          // nzOkText: "知道了",
          nzOkText: this.translate.syncGet("word.got_it"),
        });
      }
    } else {
      this.storeService.remove(
        `${opid}_lower_contract_in_seven_days_has_notified`,
        2
      );
    }
  }
}
